import { useEffect, useState, useCallback } from "react";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Alert, AlertDescription } from "../components/ui/alert";
import {
  Save,
  X,
  Phone,
  ShieldCheck,
  Info,
  ShoppingBag,
  UtensilsCrossed,
  Truck,
} from "lucide-react";
import { useToast } from "../components/ui/use-toast";
import {
  server_post_data,
  save_restaurant_setting,
  get_restaurant_setting,
} from "../ServiceConnection/serviceconnection";

export default function CustomerSetting() {
  const { toast } = useToast();

  // State management
  const [showLoaderAdmin, setShowLoaderAdmin] = useState(false);
  const [originalSettings, setOriginalSettings] = useState({
    dinein: false,
    delivery: false,
    pickup: false,
  });
  const [settings, setSettings] = useState({
    dinein: false,
    delivery: false,
    pickup: false,
  });
  const [hasChanges, setHasChanges] = useState(false);

  // Check if form is valid (at least one option selected)
  const isFormValid = settings.dinein || settings.delivery || settings.pickup;

  // Fetch settings data
  const fetchSettingsData = useCallback(async () => {
    setShowLoaderAdmin(true);
    const fd = new FormData();
    fd.append("flag", 1);
    fd.append("want_table_data", 0);

    try {
      const response = await server_post_data(get_restaurant_setting, fd);

      if (!response.data.error) {
        const data = response.data.message.data_restaurant?.[0];
        const fetchedSettings = {
          dinein: data?.phone_validation_dinein === "true",
          delivery: data?.phone_validation_ondelivery === "true",
          pickup: data?.phone_validation_pickup === "true",
        };
        setSettings(fetchedSettings);
        setOriginalSettings(fetchedSettings);
        setHasChanges(false);
      } else {
        toast({
          title: "Error",
          description: response.data.message,
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error fetching settings:", error);
      toast({
        title: "Error",
        description: "Failed to load settings data",
        variant: "destructive",
      });
    } finally {
      setShowLoaderAdmin(false);
    }
  }, [toast]);

  // Handle toggle change
  const handleToggleChange = useCallback(
    (field, checked) => {
      const newSettings = { ...settings, [field]: checked };
      setSettings(newSettings);
      setHasChanges(
        JSON.stringify(newSettings) !== JSON.stringify(originalSettings)
      );
    },
    [settings, originalSettings]
  );

  // Handle save
  const handleSave = useCallback(async () => {
    if (!isFormValid) {
      toast({
        title: "Validation Error",
        description: "Please enable at least one phone validation option",
        variant: "destructive",
      });
      return;
    }

    if (!hasChanges) {
      toast({
        title: "No Changes",
        description: "No changes to save",
      });
      return;
    }

    setShowLoaderAdmin(true);

    const fd = new FormData();
    fd.append("phone_validation_dinein", settings.dinein);
    fd.append("phone_validation_ondelivery", settings.delivery);
    fd.append("phone_validation_pickup", settings.pickup);
    fd.append("from_where", 4);

    try {
      const response = await server_post_data(save_restaurant_setting, fd);

      if (response.data.error) {
        toast({
          title: "Error",
          description: response.data.message,
          variant: "destructive",
        });
      } else {
        toast({
          title: "Success",
          description: "Customer settings saved successfully",
        });
        setOriginalSettings(settings);
        setHasChanges(false);
      }
    } catch (error) {
      console.error("Save error:", error);
      toast({
        title: "Error",
        description: "Failed to save settings",
        variant: "destructive",
      });
    } finally {
      setShowLoaderAdmin(false);
    }
  }, [settings, isFormValid, hasChanges, toast]);

  // Handle cancel
  const handleCancel = useCallback(() => {
    setSettings(originalSettings);
    setHasChanges(false);
  }, [originalSettings]);

  // Initial load
  useEffect(() => {
    fetchSettingsData();
  }, [fetchSettingsData]);

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-foreground">
          Customer Settings
        </h3>
        <p className="text-sm text-muted-foreground mt-1">
          Configure phone validation requirements for different order types
        </p>
      </div>

      {/* Phone Validation Card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Phone className="w-4 h-4 text-primary" />
            Customer Phone Validation
          </CardTitle>
          <CardDescription>
            Enable phone number validation on billing screen by order type
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Delivery */}
          <div className="flex items-center justify-between p-4 rounded-lg border border-border bg-muted/30">
            <div className="flex items-center gap-3 flex-1">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <Truck className="w-5 h-5 text-primary" />
              </div>
              <div className="space-y-1">
                <Label
                  htmlFor="delivery-switch"
                  className="text-sm font-medium cursor-pointer"
                >
                  Delivery Orders
                </Label>
                <p className="text-sm text-muted-foreground">
                  Require phone validation for delivery orders
                </p>
              </div>
            </div>
            <Switch
              id="delivery-switch"
              checked={settings.dinein}
              onCheckedChange={(checked) =>
                handleToggleChange("dinein", checked)
              }
            />
          </div>

          {/* Pick Up */}
          <div className="flex items-center justify-between p-4 rounded-lg border border-border bg-muted/30">
            <div className="flex items-center gap-3 flex-1">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <ShoppingBag className="w-5 h-5 text-primary" />
              </div>
              <div className="space-y-1">
                <Label
                  htmlFor="pickup-switch"
                  className="text-sm font-medium cursor-pointer"
                >
                  Pick Up Orders
                </Label>
                <p className="text-sm text-muted-foreground">
                  Require phone validation for pickup orders
                </p>
              </div>
            </div>
            <Switch
              id="pickup-switch"
              checked={settings.delivery}
              onCheckedChange={(checked) =>
                handleToggleChange("delivery", checked)
              }
            />
          </div>

          {/* Dine In */}
          <div className="flex items-center justify-between p-4 rounded-lg border border-border bg-muted/30">
            <div className="flex items-center gap-3 flex-1">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <UtensilsCrossed className="w-5 h-5 text-primary" />
              </div>
              <div className="space-y-1">
                <Label
                  htmlFor="dinein-switch"
                  className="text-sm font-medium cursor-pointer"
                >
                  Dine In Orders
                </Label>
                <p className="text-sm text-muted-foreground">
                  Require phone validation for dine-in orders
                </p>
              </div>
            </div>
            <Switch
              id="dinein-switch"
              checked={settings.pickup}
              onCheckedChange={(checked) =>
                handleToggleChange("pickup", checked)
              }
            />
          </div>

          {/* Info Alert */}
          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription>
              This setting enables validation of customer phone numbers on the
              billing screen. When enabled, staff will be required to enter a
              valid phone number before completing the order.
            </AlertDescription>
          </Alert>

          {/* Validation Warning */}
          {!isFormValid && (
            <Alert variant="destructive">
              <ShieldCheck className="h-4 w-4" />
              <AlertDescription>
                At least one order type must have phone validation enabled.
              </AlertDescription>
            </Alert>
          )}

          {/* Status Message */}
          {hasChanges && isFormValid && (
            <div className="flex items-center gap-2 p-3 rounded-lg bg-warning/10 border border-warning/20">
              <Phone className="w-4 h-4 text-warning" />
              <p className="text-sm text-warning">
                You have unsaved changes. Click Save to apply.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Benefits Card */}
      <Card className="border-primary/20 bg-primary/5">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
              <ShieldCheck className="w-5 h-5 text-primary" />
            </div>
            <div className="space-y-2">
              <h4 className="text-sm font-semibold text-foreground">
                Why Enable Phone Validation?
              </h4>
              <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
                <li>Ensure accurate customer contact information</li>
                <li>Reduce failed delivery attempts</li>
                <li>Enable order status notifications via SMS</li>
                <li>Build customer database for marketing</li>
                <li>Improve customer service and follow-ups</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <div className="flex items-center justify-end gap-4 pt-4 border-t">
        <Button
          variant="outline"
          onClick={handleCancel}
          disabled={showLoaderAdmin || !hasChanges}
          className="gap-2"
        >
          <X className="w-4 h-4" />
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          disabled={showLoaderAdmin || !hasChanges || !isFormValid}
          className="gap-2 gradient-primary text-primary-foreground hover:opacity-90"
        >
          {showLoaderAdmin ? (
            "Saving..."
          ) : (
            <>
              <Save className="w-4 h-4" />
              Save Settings
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
